import { CountBadgeV2 } from "@/components/interactive/CountBadgeV2";
import { ZIndexClass } from "@/constants/z-index";
import { classNames } from "@/utils/classnames";
import React, { ReactNode, useLayoutEffect, useRef, useState } from "react";

export interface ShortcutTooltipV2Key {
  value: string;
  ariaLabel?: string;
}

export interface ShortcutTooltipV2Props extends React.ComponentProps<"div"> {
  label: ReactNode;
  keys: readonly ShortcutTooltipV2Key[];
}

export interface ViewportAwareShortcutTooltipV2Props extends ShortcutTooltipV2Props {
  left: number;
  top: number;
  themeClass?: string;
  viewportGutter?: number;
}

const DEFAULT_TOOLTIP_VIEWPORT_GUTTER_PX = 8;

const TOOLTIP_SURFACE_CLASS =
  "design-system-typography inline-flex shrink-0 cursor-default rounded-cds-4 border-[1px] " +
  "border-cds-surface-border bg-cds-surface-hover text-cds-xs leading-4 " +
  "!text-cds-ink shadow-cds-m dark:border-white/[0.04] dark:bg-zinc-900 dark:!text-zinc-200 " +
  "dark:shadow-cds-l-dark";

const TOOLTIP_KBD_CLASS = "min-w-4";
const TOOLTIP_LABEL_CLASS = "min-w-0";
const TOOLTIP_WITH_KEYS_PADDING_CLASS = "pr-cds-4";
const TOOLTIP_WITHOUT_KEYS_PADDING_CLASS = "pr-cds-6";
const TOOLTIP_KBD_GROUP_CLASS = "inline-flex items-center gap-cds-2 pl-cds-4";

export interface TooltipSurfaceV2Props extends React.ComponentProps<"div"> {
  children: ReactNode;
  variant?: "content" | "shortcut" | "interactive";
}

export const TOOLTIP_FLOATING_LAYER_CLASS = "z-[10000]";

export const TooltipSurfaceV2: React.FC<TooltipSurfaceV2Props> = ({
  children,
  className,
  variant = "content",
  ...rest
}) => {
  return (
    <div
      className={classNames(
        TOOLTIP_SURFACE_CLASS,
        variant === "content"
          ? "min-h-6 max-w-80 items-start whitespace-normal px-cds-6 py-cds-4"
          : variant === "interactive"
            ? "max-w-96 items-start whitespace-normal p-1"
            : "h-6 items-center whitespace-nowrap pl-cds-6",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

const ShortcutTooltipV2Impl: React.FC<ShortcutTooltipV2Props> = ({
  label,
  keys,
  className,
  ...rest
}) => {
  const hasKeys = keys.length > 0;

  return (
    <TooltipSurfaceV2
      variant={"shortcut"}
      role={"tooltip"}
      className={classNames(
        hasKeys ? TOOLTIP_WITH_KEYS_PADDING_CLASS : TOOLTIP_WITHOUT_KEYS_PADDING_CLASS,
        className,
      )}
      {...rest}
    >
      <span className={TOOLTIP_LABEL_CLASS}>{label}</span>
      {hasKeys && (
        <span className={TOOLTIP_KBD_GROUP_CLASS}>
          {keys.map((key, index) => (
            <CountBadgeV2
              key={`${key.value}-${index}`}
              as={"kbd"}
              aria-label={key.ariaLabel}
              className={TOOLTIP_KBD_CLASS}
              withStroke
            >
              {key.value}
            </CountBadgeV2>
          ))}
        </span>
      )}
    </TooltipSurfaceV2>
  );
};

export const ShortcutTooltipV2 = React.memo(ShortcutTooltipV2Impl);
ShortcutTooltipV2.displayName = "ShortcutTooltipV2";

const ViewportAwareShortcutTooltipV2Impl: React.FC<ViewportAwareShortcutTooltipV2Props> = ({
  left,
  top,
  themeClass,
  viewportGutter = DEFAULT_TOOLTIP_VIEWPORT_GUTTER_PX,
  label,
  keys,
  ...rest
}) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [resolvedLeft, setResolvedLeft] = useState(left);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const updatePosition = () => {
      const tooltipWidth = tooltipRef.current?.getBoundingClientRect().width ?? 0;
      if (tooltipWidth <= 0) {
        setResolvedLeft(left);
        return;
      }

      const halfWidth = tooltipWidth / 2;
      const minLeft = viewportGutter + halfWidth;
      const maxLeft = window.innerWidth - viewportGutter - halfWidth;

      setResolvedLeft(Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft)));
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => window.removeEventListener("resize", updatePosition);
  }, [keys, label, left, viewportGutter]);

  return (
    <div
      ref={tooltipRef}
      className={classNames(
        themeClass,
        "pointer-events-none fixed inline-flex -translate-x-1/2",
        ZIndexClass.TOOLTIP,
      )}
      style={{ left: resolvedLeft, top }}
    >
      <ShortcutTooltipV2 label={label} keys={keys} {...rest} />
    </div>
  );
};

export const ViewportAwareShortcutTooltipV2 = React.memo(ViewportAwareShortcutTooltipV2Impl);
ViewportAwareShortcutTooltipV2.displayName = "ViewportAwareShortcutTooltipV2";
